/* External */
import axios from 'axios';

/* Ott-common */
import { createAuthenticationInterceptor } from '@dtvgo/axios-interceptor-auth';

/* Utils */
import { createClientHeadersInterceptor } from './interceptors/clientHeaders';

/**
 * @typedef AccessTokenData
 * @type {object}
 * @property {string} [idToken]
 * @property {string} accessToken
 * @property {string} refreshToken
 * @property {number} expiresIn
 * @property {number} timestamp
 */

/**
 * @typedef SessionTokenData
 * @type {object}
 * @property {string} sessionToken
 */

/**
 * @typedef Credentials
 * @type {object}
 * @property {SessionTokenData} sessionTokenData
 * @property {AccessTokenData} [accessTokenData]
 */

/**
 * @typedef ShouldRenewCredentials
 * @type {object}
 * @property {boolean} sessionToken
 * @property {boolean} accessToken
 */

/**
 * @callback RenewCredentialsCallback
 * @param {Credentials} currentCredentials
 * @param {ShouldRenewCredentials} ShouldRenewCredentials
 * @returns {Credentials | Promise<Credentials>} The new credentials
 */

/**
 * @callback GetCredentialsCallback
 * @returns {Credentials}
 */

/**
 * @callback SetCredentialsCallback
 * @param {Credentials} newCredentials
 * @returns {void}
 */

/**
 * @callback GetEntitlementsListCallback
 * @returns {string[] | Promise<string[]>}
 */

/**
 * @callback GetEntitlementsListCallback
 * @returns {string[] | Promise<string[]>}
 */

/**
 * @typedef BackendAxiosInstanceCreatorParamsType
 * @type {object}
 * @property {string} [host]
 * @property {GetCredentialsCallback} [getCredentials]
 * @property {SetCredentialsCallback} [setCredentials]
 * @property {GetEntitlementsListCallback} [getEntitlementsList]
 * @property {import('./utils').ClientType} [clientType]
 */

/**
 * @typedef BackendAxiosInstanceCreatorParams
 * @type {
    BackendAxiosInstanceCreatorParamsType
    & import('./interceptors/clientHeaders').ClientHeadersInterceptorCreatorParams
   }
 */

/**
 * @param {BackendAxiosInstanceCreatorParams} param0
 */
export default function createAxiosInstance({
  host,
  clientType,
  getCredentials,
  setCredentials,
  getBlimConsumer,
  getEntitlementsList,
  clientId,
  getAppVersion,
}) {
  /** @type {RenewCredentialsCallback} */
  let renewCredentials;
  const backendAxiosInstance = axios.create({
    baseURL: host,
    responseType: 'json',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  /**
   * @typedef GetCredentialsWithRenewParams
   * @type {object}
   * @property {boolean} withoutRenew
   */
  /**
   * @param {GetCredentialsWithRenewParams} param0
   * @param {ShouldRenewCredentials} shouldRenew
   */
  async function getCredentialsWithRenew({ withoutRenew }, shouldRenew) {
    let credentials = await getCredentials();
    if (renewCredentials && !withoutRenew) {
      const newCredentials = await renewCredentials(credentials, shouldRenew);
      if (newCredentials !== credentials) {
        if (setCredentials) await setCredentials(newCredentials);
        credentials = await getCredentials();
      }
    }

    return credentials;
  }

  /**
   * @param {GetCredentialsWithRenewParams} config
   */
  async function getSessionTokenForAuthInterceptor(config) {
    const {
      sessionTokenData: { sessionToken },
    } = await getCredentialsWithRenew(config, {
      sessionToken: true,
    });
    return sessionToken;
  }

  /**
   * @param {GetCredentialsWithRenewParams} config
   */
  async function getAccessTokenForAuthInterceptor(config) {
    const {
      accessTokenData: { accessToken },
    } = await getCredentialsWithRenew(config, {
      accessToken: true,
    });
    return accessToken;
  }

  /**
   * @typedef ThinkAnalyticsRequestConfigProperties
   * @type {object}
   * @property {boolean} [includeTimeZone]
   * @property {boolean} [includeEntitlements]
   * @property {boolean} [includeClientType]
   */
  /**
   * @typedef ThinkAnalyticsRequestConfig
   * @type {import('axios').AxiosRequestConfig & ThinkAnalyticsRequestConfigProperties}
   */
  /**
   * Adds the params required by think analytics service to an axios request config based
   * on if the config has the properties added to do that. This function can be used as
   * an axios request interceptor
   * @param {ThinkAnalyticsRequestConfig} thinkAnalyticsRequestConfig - the current config
   */
  async function addThinkAnalyticsParamsToRequest(thinkAnalyticsRequestConfig) {
    const {
      includeTimeZone,
      includeClientType,
      includeEntitlements,
      method,
      ...axiosRequestConfig
    } = thinkAnalyticsRequestConfig;

    const useData = method === 'post' || method === 'put' || method === 'patch';
    let newParams = axiosRequestConfig[useData ? 'data' : 'params'];

    if (!newParams) {
      newParams = {};
    }

    if (includeTimeZone) {
      const match = /GMT([+-]?\d{2})(\d{2})/.exec((new Date()).toString());
      newParams.timeZone = `${match[1]}:${match[2]}`;
    }

    if (includeClientType) {
      newParams.clientType = clientType;
    }

    if (includeEntitlements && getEntitlementsList) {
      newParams.entitlements = await getEntitlementsList();
      newParams.entitlements = useData ? newParams.entitlements : newParams.entitlements.join(',');
    }

    return {
      method,
      ...axiosRequestConfig,
      [useData ? 'data' : 'params']: newParams,
    };
  }

  const authenticationInterceptor = createAuthenticationInterceptor({
    getSessionToken: getSessionTokenForAuthInterceptor,
    getAccessToken: getAccessTokenForAuthInterceptor,
    getConsumer: getBlimConsumer,
  });
  const clientHeadersInterceptor = createClientHeadersInterceptor({
    clientId,
    getAppVersion,
  });
  backendAxiosInstance.interceptors.request.use(authenticationInterceptor);
  backendAxiosInstance.interceptors.request.use(addThinkAnalyticsParamsToRequest);
  backendAxiosInstance.interceptors.request.use(clientHeadersInterceptor);

  /**
   * @callback SetRenewCredentialsType
   * @param {RenewCredentialsCallback} newRenewCredentials
   */
  /**
   * @type {SetRenewCredentialsType}
   */
  backendAxiosInstance.setRenewCredentialsFunction = (newRenewCredentials) => {
    renewCredentials = newRenewCredentials;
  };

  return backendAxiosInstance;
}
